import React, { useMemo, useState } from 'react';
import { useAuth } from '../../systems/auth-context';
import { Scenario, Scene } from '../../types';
import { generateAiBlock } from './ai-block-api';
import {
  AI_BLOCK_GUIDES,
  AI_BLOCK_TYPE_OPTIONS,
  type AiBlockContext,
  type AiBlockType,
} from './ai-block-guides';
import {
  AI_BLOCK_PREVIEW_SCENE_ID,
  buildAiBlockPreviewScenario,
} from './ai-block-preview';
import { PreviewModal } from './PreviewModal';
import inline from './inline-editor.module.css';
import studio from './scenario-studio.module.css';

function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

interface InlineAiGenerateFormProps {
  onInsert: (body: Omit<Scene, 'id'>) => void;
  onCancel: () => void;
  context?: AiBlockContext;
  scenario?: Scenario;
  scenarioId?: string;
  compact?: boolean;
  onOpenSettings?: () => void;
}

export const InlineAiGenerateForm: React.FC<InlineAiGenerateFormProps> = ({
  onInsert,
  onCancel,
  context,
  scenario,
  scenarioId,
  compact = false,
  onOpenSettings,
}) => {
  const { user } = useAuth();
  const [blockType, setBlockType] = useState<AiBlockType>('phoneMail');
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Omit<Scene, 'id'> | null>(null);
  const [gamePreviewOpen, setGamePreviewOpen] = useState(false);

  const guide = AI_BLOCK_GUIDES[blockType];

  const exampleButtons = useMemo(() => guide.examples, [guide.examples]);

  const previewScenario = useMemo(() => {
    if (!preview || !scenario) return null;
    return buildAiBlockPreviewScenario(scenario, preview);
  }, [preview, scenario]);

  const canPreviewBlock = Boolean(preview && scenario && scenarioId);

  const handleGenerate = async () => {
    if (!user) {
      setError('Connexion requise (admin DarwinX)');
      return;
    }
    if (!prompt.trim()) {
      setError('Décrivez le bloc à générer');
      return;
    }

    setLoading(true);
    setError(null);
    setPreview(null);
    setGamePreviewOpen(false);

    try {
      const result = await generateAiBlock(blockType, prompt, context);
      setPreview(result.body as Omit<Scene, 'id'>);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  const handleInsert = () => {
    if (!preview) return;
    onInsert(preview);
    setPrompt('');
    setPreview(null);
    setGamePreviewOpen(false);
    setError(null);
  };

  const form = (
    <div className={inline.aiForm}>
      <div className={inline.aiFormHeader}>
        <span className={inline.addFormLabel}>Générer par IA</span>
        {preview && (
          <span className={inline.aiGeneratedStatus} role="status">
            <span className={inline.aiGeneratedCheck} aria-hidden>
              ✓
            </span>
            Généré
          </span>
        )}
        <select
          className={studio.select}
          value={blockType}
          onChange={(e) => {
            setBlockType(e.target.value as AiBlockType);
            setPreview(null);
            setGamePreviewOpen(false);
            setError(null);
          }}
        >
          {AI_BLOCK_TYPE_OPTIONS.map((opt) => (
            <option key={opt.type} value={opt.type}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className={inline.aiGuide}>
        <p className={inline.aiGuideSection}>
          <strong>Comment ça marche</strong> — {guide.howItWorks}
        </p>
        <p className={inline.aiGuideSection}>
          <strong>Mise en situation</strong> — {guide.situation}
        </p>
        <div className={inline.aiExamples}>
          <span className={inline.aiExamplesLabel}>Exemples de prompts :</span>
          <div className={inline.aiExampleChips}>
            {exampleButtons.map((example) => (
              <button
                key={example}
                type="button"
                className={inline.aiExampleChip}
                onClick={() => setPrompt(example)}
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      </div>

      <label className={inline.aiPromptLabel}>
        Votre prompt
        <textarea
          className={`${studio.input} ${inline.aiPrompt}`}
          rows={compact ? 3 : 4}
          placeholder={guide.placeholder}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
      </label>

      {error && (
        <div className={inline.aiErrorWrap}>
          <p className={inline.aiError}>{error}</p>
          {error.includes('Clé API Anthropic') && onOpenSettings && (
            <button type="button" className={studio.btnSecondary} onClick={onOpenSettings}>
              Ouvrir les paramètres
            </button>
          )}
        </div>
      )}

      {preview && (
        <details className={inline.aiPreview}>
          <summary>Aperçu JSON généré</summary>
          <pre className={inline.aiPreviewCode}>{JSON.stringify(preview, null, 2)}</pre>
        </details>
      )}

      <div className={inline.aiActions}>
        <button
          type="button"
          className={studio.btnPrimary}
          onClick={preview ? handleInsert : handleGenerate}
          disabled={loading || (!preview && !prompt.trim())}
        >
          {loading ? 'Génération…' : preview ? 'Insérer le bloc' : 'Générer'}
        </button>
        {preview && canPreviewBlock && (
          <button
            type="button"
            className={`${studio.btnSecondary} ${inline.aiPreviewBtn}`}
            onClick={() => setGamePreviewOpen(true)}
            disabled={loading}
            title="Prévisualiser le bloc généré"
          >
            <EyeIcon />
            Préview
          </button>
        )}
        {preview && (
          <button
            type="button"
            className={studio.btnSecondary}
            onClick={() => {
              setPreview(null);
              handleGenerate();
            }}
            disabled={loading}
          >
            Régénérer
          </button>
        )}
        <button type="button" className={studio.btnGhost} onClick={onCancel}>
          Annuler
        </button>
      </div>
    </div>
  );

  const content = (
    <>
      {form}
      {gamePreviewOpen && previewScenario && scenarioId && (
        <PreviewModal
          scenario={previewScenario}
          scenarioId={scenarioId}
          startSceneId={AI_BLOCK_PREVIEW_SCENE_ID}
          onClose={() => setGamePreviewOpen(false)}
        />
      )}
    </>
  );

  if (compact) return content;
  return <div className={inline.insertBelow}>{content}</div>;
};
